<template>
  <div>
    <select-field ref="NumeroObjetSummary"
                  :value=display
                  :required=required
                  :editFieldInfo=editFieldInfo
                  :items=items
                  :disabled=readOnly
                  :canEdit=canEdit
                  :selectText=selectText
                  :hideLabel='hideLabel'
                  :dataTable='dataTable'
                  :showMissing=showMissing
                  :notOrdered=notOrdered
                  :fiche=fiche
                  @updated=setUpdated
                  @onEdit=onEdit
                  @error=setError>
    </select-field> 

    <section-container-panel v-if="dialogOpen"
                             :section="section"
                             :dialogOpen="dialogOpen"
                             :extended="true"
                             :helper="helper"
                             :ressourceHeader="ressourceHeader"
                             :fiche="fiche"
                             @updated="setSectionUpdated"
                             @closeDialog="closeDialog"
                             @saveOrCancel="setSaveOrCancel"
                             @status="statusUpdated">
    </section-container-panel>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import Section from '@/framework/base/classes/Section';
  import { FieldInfo, EditFieldType, FieldStatus } from '@/framework/base/components/FieldInfo'
  import SummaryBase from '@/framework/base/components/SummaryBase';
  import { Sections } from '@/generated/classes/AppSchema';
  import SelectField from '@/framework/base/components/SelectField.vue'
  import Item from '@/framework/base/classes/Item';
  import NumeroObjet from '@/extend/classes/NumeroObjet';
  import Owner from '@/extend/classes/Owner';
  import SectionContainerPanel from '@/framework/base/components/SectionContainerPanel.vue';
  import SectionNumeroObjetExtend from '@/extend/sections/SectionNumeroObjetExtend.vue';
  import { storeNumeroObjet } from '@/generated/stores';
  import ListData, { ListFilter } from '@/extend/classes/ListData';

  export default defineComponent({
    extends: SummaryBase,
    components: {
      SelectField,
      SectionContainerPanel,
    },

    props: {
      helper: {
        type: Boolean
      }
    },

    beforeUnmount (): void {
      this.clearStore();
    },

    data() {
      const listNumeroObjet: NumeroObjet[] = [];
      const tmpNumeroObjet: NumeroObjet = new NumeroObjet();
      return {
        selectedNumeroObjetId: 0,
        selectedNumeroObjet: new NumeroObjet(),
        tmpNumeroObjet,
        newContext: true,
        reset: true,
        listUpdated: false,
        listNumeroObjet
      }
    },

    computed: {
      editFieldInfo (): FieldInfo {
        const type = EditFieldType.SUMMARY;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
      },

      section (): Section {
        const section = new Section();
        section.component = SectionNumeroObjetExtend;
        section.type = 'numeroObjet';
        section.data = this.selectedNumeroObjet;
        section.picto = 'SectionNumeroObjetExtendPicto';
        section.contextId = this.contextId;
        section.showButton = true;
        section.autoSave = true;
        if (this.sectionName) {
          section.sectionName = this.sectionName;
        }
        if (this.requiredAttributs) {
          section.requiredAttributs = this.requiredAttributs;
        }
        return section;
      },

      listItems (): NumeroObjet[] {
        if (this.reset != this.toggleReset) {
          this.loadItems();
        }
        return this.listNumeroObjet;
      },

      items (): Item[] {
        const items: Item[] = [];
        items.push(new Item(0, this.newLabel != undefined && this.newLabel != '' ? this.newLabel : "Nouveau numéro d'objet")); 
        const numeroObjets = this.getItems(this.listItems, this.sectionName);
        numeroObjets.forEach(numeroObjet => {
          if (numeroObjet && numeroObjet.id > 0 ) {
            items.push(new Item(numeroObjet.id, this.getDisplay(numeroObjet)));
          }
        })
        return items;
      },

      display (): string {
        const value = this.value;
        let item = this.items.find(a => a.key == value)
        this.changeContext();
        if (this.items.length == 2 && this.newContext && this.required && this.autoSelect) {
          this.changeContext();
          item = this.items.find(a => a.key == this.items[1].key);
          if (item && item.key != this.initValue) {
            this.itemChanged(item.key);
            this.setStatusUpdated(true);
            if (this.initValue != 0 || this.items.length != 2) {
              this.statusUpdated(this.status);
            }
          }
        }
        if (item) {
          if (this.resetToggle()) {
            this.setStatusUpdated(true);
            this.statusUpdated(this.status);
          }
          const itemDefined = item;
          const numeroObjet = this.listNumeroObjet.find(a => a.id == itemDefined.key);
          if (numeroObjet && numeroObjet.id != this.selectedNumeroObjetId) {
            this.setSelectedNumeroObjet(numeroObjet);
          }
          if (item.key == 0 && this.selectedNumeroObjetId == 0) {
            this.setSelectedNumeroObjet(this.newNumeroObjet());
          }
          this.setSelectedNumeroObjetId(numeroObjet ? numeroObjet.id : this.selectedNumeroObjet.id);
        }
        const res = item && item.key != 0 ? item.title : '';
        if (this.cancelChanges && item) {
          this.itemChanged(item.key);
        }
        this.setAttribut(this.selectedNumeroObjet, this.owner, this.parentObj)
        return res
      },

      canEdit (): string {
        return true
      }
    },

    methods: {
      setSelectedNumeroObjet(value: NumeroObjet) {
        this.selectedNumeroObjet = value;
      },
      setSelectedNumeroObjetId(id: number) {
        this.selectedNumeroObjetId = id;
      },
      setNewContext(value: boolean) {
        this.newContext = value;
      },
      setStatusUpdated(value: boolean) {
        this.status.updated = value;
      },
      newNumeroObjet (): NumeroObjet {
        this.clearStore();
        const newNumeroObjet = storeNumeroObjet.createNewNumeroObjet();
        this.tmpNumeroObjet = newNumeroObjet;
        return newNumeroObjet;
      },

      loadItems (): Promise<boolean> {
        this.reset = this.toggleReset;
        return new Promise((resolve) => {
          if (this.filter) {
            NumeroObjet.list(this.filter)
              .then((data: ListData) => {
                const items = data.reccords as NumeroObjet[];
                this.listNumeroObjet = [];
                items.forEach((item) => {
                  this.listNumeroObjet.push(NumeroObjet.fromJson(item));
                })
                this.listUpdated = true;
                resolve(true)
              }).catch(() => {
                //Catch empty list
              });
          } else {
            resolve(false);
          }
        })
      },

      clearStore() : void {
        if (this.tmpNumeroObjet != null && this.tmpNumeroObjet.id < 0) {
          storeNumeroObjet.mNumeroObjetDeleted(this.tmpNumeroObjet.id);
        }
      },

      contextChanged() : void {
        this.newContext = true;
        this.selectedNumeroObjetId = 0;
        this.selectedNumeroObjet = new NumeroObjet();
        this.initValue = this.value;
      },

      setSaveOrCancel () {
        this.cancelChanges = true;
      },

      // eslint-disable-next-line
      getItems(numeroObjet: NumeroObjet[], section: String): NumeroObjet[] {
        const items: NumeroObjet[] = [];
        numeroObjet.forEach((item) => {
          items.push(NumeroObjet.fromJson(item));
        })
        return items;
      },

        // eslint-disable-next-line
      getDisplay(numeroObjet: NumeroObjet): string {
        return '';
      },

      // eslint-disable-next-line
      setAttribut(numeroObjet: NumeroObjet, owner: Owner, parentObj: unknown): void {
        // to implement
      },

      setSectionUpdated(numeroObjet: NumeroObjet) {
        const item = this.items.find(a => a.key == numeroObjet.id);
        if (!item && numeroObjet.id > 0) {
          this.loadItems().then(() => {
            const item = this.items.find(a => a.key == numeroObjet.id);
            if (item) {
              this.setUpdated(item);
            }
          });
        }
        if (item) {
          this.setUpdated(item);
        }
      },

      setUpdated (item: Item): void {
        if (item.key == 0) {
          this.dialogOpen = true;
          this.selectedNumeroObjet = this.newNumeroObjet();
        }
        this.itemChanged(item.key);
        this.status.updated = this.initValue != item.key;
        this.status.error = false;
        this.statusUpdated(this.status);
      },

      setRestored (numeroObjet: NumeroObjet) {
        this.selectedNumeroObjet = numeroObjet;
      },

      itemChanged (id: number): void {
        this.$emit('update:value', id);
      },

      statusUpdated (status: FieldStatus): void {
        this.$emit('status', status);
      },

      missing (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.NumeroObjetSummary.missing();
      },

      touch (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.NumeroObjetSummary.touch();
      }
    },
  })
</script>
